import { ConditionalRender } from '@/components/ConditionalRender';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import { Link } from '@/components/Link';
import { OverflowText } from '@/components/OverflowText';
import { Column } from '@/components/table/types';
import { Tooltip } from '@/components/Tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/utils/classname';
import { formatDate } from '@/utils/date.util';

export function TableCellContent<TData>(props: {
  inspecting: boolean;
  col: Column<TData>;
  item: TData;
  onClick?: () => void;
  isLoading?: boolean;
  selectedRowsData: TData[];
}) {
  const { col, item, selectedRowsData, inspecting, isLoading } = props;

  if (isLoading) {
    return <Skeleton />;
  }

  function getCell() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const itemContent: any = (item as unknown as Record<any, any>)[col.id];
    const content =
      typeof itemContent === 'string'
        ? itemContent
        : itemContent === undefined
          ? '-'
          : `${itemContent}`;

    if (typeof col.cell === 'function') {
      const cellContent = col.cell(item, selectedRowsData);

      if (typeof cellContent === 'string') {
        return <OverflowText text={cellContent || '-'} />;
      } else {
        // Force cellContent to use text-chariot-text-primary if inspecting
        if (inspecting) {
          return <div className="force-primary-text">{cellContent}</div>;
        }
        return cellContent;
      }
    }

    if (col.cell === 'highlight') {
      return (
        <div className="text-brand w-full font-medium">
          <OverflowText text={content || '-'} />
        </div>
      );
    }

    if (col.cell === 'date' && content) {
      return (
        <Tooltip title={content} placement="bottom-end">
          <span
            data-testid="date"
            data-lastseen={content}
            className="text-chariot-text-secondary"
          >
            {formatDate(content)}
          </span>
        </Tooltip>
      );
    }

    return <OverflowText text={content || '-'} />;
  }

  return (
    <div
      className={cn(
        'w-full',
        col.align === 'center' && 'flex justify-center',
        col.onClick && 'relative cursor-pointer'
      )}
      onClick={props.onClick}
    >
      <ConditionalRender
        condition={Boolean(col.copy)}
        conditionalWrapper={children => {
          return <CopyToClipboard>{children}</CopyToClipboard>;
        }}
      >
        <ConditionalRender
          condition={Boolean(col.to)}
          conditionalWrapper={children => {
            return (
              <Link
                to={col.to?.(item) || ''}
                onClick={event => {
                  event.stopPropagation();
                }}
                className="flex overflow-hidden"
                buttonClass="flex overflow-hidden p-0"
              >
                {children}
              </Link>
            );
          }}
        >
          {getCell()}
        </ConditionalRender>
      </ConditionalRender>
    </div>
  );
}
